import {Person, ContactDetailsValidation} from '../lib/interfaces';

/**
 * This regular expression considers almost all edge cases according to the standard RFC 5322.
 * @param email
 * @returns
 */
export function isValidEmail(email: string) {
  var re =
    /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
  return re.test(email.toLowerCase());
}

/**
 * This regular expression considers almost all edge cases according to the standard RFC 5322.
 * @param year
 * @param month
 * @param day
 * @returns
 */
export function isValidDate(year: number, month: number, day: number): boolean {
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/**
 * This function checks is the PIN is valid according to the Luhn algorithm.
 * @param personId
 * @returns
 */
export function isValidPersonalIdentityNumber(personId?: string): boolean {
  if (!personId || personId.length !== 12) {
    return false;
  }

  const year = parseInt(personId.substring(0, 4));
  const month = parseInt(personId.substring(4, 6));
  let day = parseInt(personId.substring(6, 8));

  if (day > 60) {
    // This is a samordningsnummer, adjust the day
    day = day - 60;
  }

  if (!isValidDate(year, month, day)) {
    return false;
  }

  // Validate the last four digits using the Luhn algorithm
  const n = personId.split('').map(Number);
  let luhnSum = 0;
  for (let i = 2; i < n.length - 1; i++) {
    const val = n[i];
    if (i % 2 === 0) {
      luhnSum += val > 4 ? val * 2 - 9 : val * 2;
    } else {
      luhnSum += val;
    }
  }

  return (10 - (luhnSum % 10)) % 10 === n[n.length - 1];
}

export function isContactDetailsInvalidated(
  person1: Person,
  person2: Person,
  validationFields: ContactDetailsValidation[]
): boolean {
  // Check if person1 has valid fields and if any invalid fields exist overall
  const isPerson1MissingInfo = !(person1.email && person1.phone);
  const anyInvalidFieldsExisting = validationFields.some((x) => x.isValid === false);
  if (isPerson1MissingInfo || anyInvalidFieldsExisting) return true;

  // Check if person2 has valid PIN if inserted
  if (person2.personalIdentityNumber) {
    const isPerson2PinInvalid = !isValidPersonalIdentityNumber(person2.personalIdentityNumber);
    const isPerson2MissingInfo = !(person2.email && person2.phone);
    return isPerson2PinInvalid || isPerson2MissingInfo;
  }

  // Check if person2 extended is valid
  if (person2.personExtended) {
    const isPerson2MissingInfo = !(person2.email && person2.phone);
    const isPerson2ExtendedMissingInfo = !(
      person2.personExtended.address &&
      person2.personExtended.zipCode &&
      person2.personExtended.city &&
      person2.personExtended.country &&
      person2.personExtended.dateOfBirth
    );
    return isPerson2MissingInfo || isPerson2ExtendedMissingInfo;
  }

  return true;
}
